import { FC } from 'react'
import * as React from 'react'
import { CalendarEvent } from '../../services/calendar'
import { buildWeekData, eventChipClass, HOUR_HEIGHT } from './calendar-layout'

interface CalendarWeekViewProps {
  weekStart: string
  events: CalendarEvent[]
  onCreate: (date: string) => void
  onOpenEvent: (event: CalendarEvent) => void
}

/**
 * Grade semanal no estilo do Google Calendar — a mesma da agenda do studio.
 *
 * Em telas estreitas a grade rola na horizontal em vez de espremer sete
 * colunas: com menos de ~70px por dia nada mais é legível.
 */
const CalendarWeekView: FC<CalendarWeekViewProps> = ({ weekStart, events, onCreate, onOpenEvent }) => {
  const week = buildWeekData(weekStart, events)

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className="flex border-b border-slate-200 bg-slate-50">
            <div className="w-14 shrink-0" />
            {week.days.map(day => (
              <div
                key={day.date}
                onClick={() => onCreate(day.date)}
                className="flex-1 cursor-pointer border-l border-slate-200 py-2 text-center hover:bg-slate-100"
              >
                <p
                  className={`text-[11px] font-semibold uppercase ${
                    day.isToday ? 'text-secondary-pure' : 'text-slate-400'
                  }`}
                >
                  {day.weekday}
                </p>
                <p
                  className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                    day.isToday ? 'bg-secondary-pure text-white' : 'text-slate-700'
                  }`}
                >
                  {day.day}
                </p>
              </div>
            ))}
          </div>

          {week.hasAllDay && (
            <div className="flex border-b border-slate-200 bg-slate-50/40">
              <div className="flex w-14 shrink-0 items-center justify-center text-[9px] uppercase text-slate-400">
                dia todo
              </div>
              {week.days.map(day => (
                <div key={day.date} className="flex-1 space-y-0.5 border-l border-slate-200 p-1">
                  {day.allDayEvents.map(event => (
                    <button
                      key={`${event.id}-${day.date}`}
                      type="button"
                      onClick={stopped(() => onOpenEvent(event))}
                      className={`block w-full truncate rounded px-1 py-0.5 text-left text-[10px] font-medium ${eventChipClass(
                        event.color,
                      )}`}
                    >
                      {event.display_title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div className="flex max-h-[70vh] overflow-y-auto">
            <div className="w-14 shrink-0">
              {week.hours.map(hour => (
                <div key={hour} style={{ height: HOUR_HEIGHT }} className="relative">
                  <span className="absolute -top-1.5 right-1.5 text-[10px] text-slate-400">{hour}</span>
                </div>
              ))}
            </div>

            {week.days.map(day => (
              <div
                key={day.date}
                onClick={() => onCreate(day.date)}
                style={{ height: week.gridHeight }}
                className={`relative flex-1 cursor-pointer border-l border-slate-200 ${
                  day.isToday ? 'bg-secondary-light/30' : ''
                }`}
              >
                {week.hours.map((hour, index) => (
                  <div
                    key={hour}
                    className="absolute inset-x-0 border-b border-slate-100"
                    style={{ top: index * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                  />
                ))}

                {day.events.map(({ event, top, height, leftPct, widthPct }) => (
                  <button
                    key={`${event.id}-${day.date}-${top}`}
                    type="button"
                    onClick={stopped(() => onOpenEvent(event))}
                    style={{
                      top,
                      height,
                      left: `${leftPct}%`,
                      width: `calc(${widthPct}% - 3px)`,
                    }}
                    className={`absolute z-[1] overflow-hidden rounded px-1 py-0.5 text-left leading-tight shadow-sm ${eventChipClass(
                      event.color,
                    )}`}
                  >
                    <span className="block truncate text-[10px] font-semibold">{event.display_title}</span>
                    <span className="block text-[9px] opacity-90">
                      {event.starts_at}–{event.ends_at}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/** A coluna do dia abre o formulário; o evento em cima dela abre os detalhes. */
function stopped(action: () => void) {
  return (event: React.MouseEvent) => {
    event.stopPropagation()
    action()
  }
}

export default CalendarWeekView
